/*
 * This code defines a Toolbar component, providing navigation
 * and user interaction features such as division selection,
 * user settings, FAQ access, and notifications,incorporating
 * hooks for dynamic state management and interaction handling
 */

import { useEffect, useState, useRef } from "react";
import { GetSVG } from "../../components/images/getSVG/getSVG";
import { useSelector } from "react-redux";
import { getAuth, setAuthDivision } from "../../redux/auth/authSlice";
import { ROUTES } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import { useSidebar } from "../sidebar/sidebarContext";
import {
  FormControl,
  Select,
  OutlinedInput,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  createTheme,
  ThemeProvider,
  Box,
} from "@mui/material";
import "./toolbar.scss";
import { useDispatch } from "react-redux";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import { UserRole, isReportsUser } from "../../utils/permissions";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useDivisionFeatureFlags } from "../../stateContext/featureFlags/FeatureFlagProvider";
import { useAllDivisionData } from "../../reactQuery/division/divisionsQuery";
import { Division as DivisionType } from "../../api/divisions/types";
import { FullStory } from '@fullstory/browser';
import { useDialogBoxContext, divSettingsState } from "../../components/decisionDialogBox/decisionDialogBoxContext";
import { decisionDialogBoxTitle, decisionDialogBoxMessage } from "../../components/decisionDialogBox/decisionDialogBox";
import Avatar from '@mui/material/Avatar';
// import { useProfilePhoto } from "../../reactQuery/user/profilePhotoQuery";
// import { useMsal } from "@azure/msal-react";

export const Toolbar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useSelector(getAuth);
  const [showUserInfo, setShowUserInfo] = useState(false);
  const ref = useRef(null);
  const [dropdownDivisions, setDropdownDivisions] = useState<DropdownDivision[]>([]);
  const [selectedDropdownDivision, setSelectedDropdownDivision] = useState<string>("");
  const currentPath = window?.location?.pathname;
  const { data: divisions } = useAllDivisionData();
  const isDivAdmin = dropdownDivisions.find((item) => item.value === selectedDropdownDivision)?.roles.includes("DivisionAdmin");
  const { divisionSettings } = useDivisionFeatureFlags(userInfo?.userDivCode || "");
  const { showDecisionDialogBox } = useDialogBoxContext();
  // const { instance } = useMsal();
  // const accountId = instance.getActiveAccount()?.homeAccountId || instance.getAllAccounts()[0]?.homeAccountId;
  // const { data: profilePhoto, isLoading: photoLoading } = useProfilePhoto(instance, accountId);

  interface DropdownDivision {
    id: string;
    label: string;
    value: string;
    name: string;
    code: string;
    roles: UserRole[];
  }

  /*
   * Custom hook to manage clicks outside of the user dropdown,
   * to auto-close it
   */
  const useOutsideAlerter = (ref: any) => {
    useEffect(() => {
      function handleClickOutside(event: any) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShowUserInfo(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  };

  useOutsideAlerter(ref);

  /*
   * Use the custom hook to toggle sidebar visibility
   */
  const { toggleSidebar, isExpanded } = useSidebar();

  /*
   * ITEM_HEIGHT and ITEM_PADDING_TOP are constants defining the dimensions of the menu items.
   * MenuProps is an object that contains the PaperProps key, which is an object that contains the style key.
   */
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    // Force the root popover container above any sticky headers/toolbar
    slotProps: {
      root: {
        sx: { zIndex: 20000 },
      },
    },
    PaperProps: {
      sx: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  useEffect(() => {
    if (!userInfo) return;
    if (!divisions || divisions.counts === "0") {
      return;
    }
    /*
     * Maps over uniqueDivisionsFromGroups to create an array of objects containing division details,
     * including id, label, value, name, and code, by looking up division names and matching division codes from divisionNameMap and allDivisions.
     */
    const divisionsData = userInfo?.divisions
      .map((userDiv: { userDivCode: string; roles: UserRole[] }) => {
        const division = divisions.data.find((d: DivisionType) => d.divisionCode.toUpperCase() === userDiv.userDivCode.toUpperCase());
        const divData = {
          id: division?.id || "",
          label: `${division?.divisionCode} - ${division?.divisionName}`,
          value: division?.divisionCode,
          name: division?.divisionName,
          code: userDiv.userDivCode,
          roles: userDiv.roles,
        };
        const displayDivision = division?.status ? divData : null;
        return displayDivision;
      })
      .filter((div: DropdownDivision | null) => div !== null);
    setDropdownDivisions(divisionsData);

    // /*
    //  * Checking if there are any divisions available in the divisionsData array and if no
    //  * division has been selected in the selectedDropdownDivision variable.
    //  * If both conditions are true, it sets the defaultDivision to the first element in the divisionsData array.
    //  */
    if (divisionsData.length > 0 && !selectedDropdownDivision) {
      // Use the division code from Redux if available (e.g. from localStorage on refresh), otherwise default to the first one
      const initialDiv = divisionsData.find((d: DropdownDivision) => d.value === userInfo.userDivCode) || divisionsData[0];
      setSelectedDropdownDivision(initialDiv.code);
      
      /* If userDivCode is not already set in Redux, update it to match the dropdown selection */
      if (!userInfo.userDivCode && initialDiv.value) {
        dispatch(
          setAuthDivision({
            code: initialDiv.value,
            name: initialDiv.name,
          })
        );
      }
    }
  }, [userInfo, divisions, dispatch, selectedDropdownDivision]);

  /*
   * This function is simply used to verify the groups the user is a part of.
   * Can be removed POST TESTING.
   */
  const logSelectedDivisionInfo = (division: string) => {
    if (!userInfo) {
      return;
    }
    const currentEnv = process.env.REACT_APP_RBAC_ENV?.toUpperCase();
    if (!currentEnv) {
      return;
    }
    const EXCLUDED_GROUPS = ["DEV", "QA", "UAT", "PROD", "ADMIN"];
    // eslint-disable-next-line
    const matchedGroups = (userInfo.userGroups || []).filter((group: string) => {
      const parts = group.split("-");
      const groupEnv = parts[5]?.toUpperCase();
      const groupDivision = parts[4]?.toUpperCase();
      return (
        groupDivision === division.toUpperCase() && groupEnv === currentEnv && !EXCLUDED_GROUPS.includes(groupDivision)
      );
    });
  };

  /*
   * This function updates the 'selectedDropdownDivision'
   * state with the value selected from the dropdown menu.
   */
  const proceedDivisionChange = (selectedDivision: DropdownDivision) => {
    dispatch(
      setAuthDivision({
        code: selectedDivision.value,
        name: selectedDivision.name,
      })
    );
    setSelectedDropdownDivision(selectedDivision.value as string);
    logSelectedDivisionInfo(selectedDivision.value as string);

    // On division change, always redirect to the appropriate dashboard based on user roles
    if (isReportsUser(selectedDivision.roles)) {
      navigate(ROUTES.closings.reports.route, { replace: true });
    } else {
      navigate(ROUTES.closings.myConcierge.route, { replace: true });
    }
  };

  const handleChange = (event: SelectChangeEvent) => {
    const selectedDivision = dropdownDivisions.filter((item) => item.value === event.target.value)[0];
    if (!selectedDivision) return;
    if (divSettingsState.unsaved) {
      showDecisionDialogBox(
        true,
        decisionDialogBoxTitle,
        decisionDialogBoxMessage,
        () => {
          divSettingsState.save();
          divSettingsState.unsaved = false;
          proceedDivisionChange(selectedDivision);
        },
        () => {
          divSettingsState.cancel();
          divSettingsState.unsaved = false;
          proceedDivisionChange(selectedDivision);
        }
      );
      return;
    }
    proceedDivisionChange(selectedDivision);
  };

  useEffect(() => {
    /* Redirects user based on their role and current path */
    const selectedDivision = dropdownDivisions.filter((item) => item.value === selectedDropdownDivision)[0];
    if (!selectedDivision) return;
    // If no division is selected, do no redirects
    if (isReportsUser(selectedDivision.roles) && currentPath !== ROUTES.closings.reports.route) {
      navigate(ROUTES.closings.reports.route, { replace: true });
    } else if (selectedDivision.roles.length === 0) {
      navigate(ROUTES.permissionDenied.permissionDenied.route, {
        replace: true,
      });
    }
  }, [navigate, dropdownDivisions, selectedDropdownDivision, currentPath]);

  // Initialize FullStory with user properties
  useEffect(() => {
    FullStory('setProperties', {
      type: 'user',
      properties: {
        division: selectedDropdownDivision,
        userId: userInfo?.localAccountId,
      }
    });
  }, [selectedDropdownDivision, userInfo?.localAccountId]);

  const theme = createTheme({
    typography: {
      fontFamily: "karla, sans-serif",
      fontSize: 14,
    },
  });

  /*
   * Rendering the toolbar with division Dropdown,
   * FAQ, Settings, Notifications, and User Avatar
   */
  return (
    <ThemeProvider theme={theme}>
      <div className="toolbar">
        <div className="align">
          <span className="icon-container">
            <Tooltip title={isExpanded ? "Collapse menu" : "Expand menu"}>
              <MenuOutlinedIcon
                style={{
                  verticalAlign: "middle",
                  color: "#FFF",
                  cursor: "pointer",
                }}
                onClick={toggleSidebar}
              />
            </Tooltip>
          </span>
          <label
            className="title"
            style={{ fontFamily: "Reckless", fontWeight: "normal" }}
            data-testid="app-title"
          >
            Closings Expediter
          </label>
        </div>
        <div className="align action-items">
          <div className="relative">
            <div className="division">
              <div className="division-content">
                <GetSVG
                  svgName={"Place"}
                  color="blue"
                  style={{ marginRight: "6px" }}
                />
                {/*
                 * This code creates a styled form control with a dropdown menu.
                 * The dropdown allows single selection and displays a placeholder when empty.
                 * The selected value and change handler are bound to the 'selectedDropdownDivision' state and
                 * 'handleChange' function, respectively. The dropdown is disabled if the user is not part of the admin group.
                 * The dropdown options are generated from the 'dropdownDivisions' array.
                 */}
                Division:
                <FormControl
                  style={{
                    width: "150px",
                    fontFamily: "karla, sans-serif",
                  }}
                >
                  <Select
                    style={{ height: "23px", fontFamily: "karla, sans-serif" }}
                    multiple={false}
                    displayEmpty
                    value={selectedDropdownDivision}
                    onChange={handleChange}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      return selected
                        ? dropdownDivisions.find(
                          (div) => div.value === selected
                        )?.label
                        : "Change Division";
                    }}
                    MenuProps={MenuProps}
                    inputProps={{ "aria-label": "Without label" }}
                    disabled={dropdownDivisions.length <= 1}
                    /* The below lines are used to remove the border around the division dropdown */
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  >
                    <MenuItem disabled value="">
                      Change Division
                    </MenuItem>
                    {dropdownDivisions.map((div) => (
                      <MenuItem
                        key={div.value}
                        value={div.value}
                        style={{ fontFamily: "karla, sans-serif", padding: 0 }}
                      >
                        <Tooltip 
                          title={
                            div.roles.includes("DivisionAdmin") 
                              ? "Division Admin" 
                              : div.roles.includes("Concierge") 
                                ? "Concierge Role" 
                                : div.roles.includes("Reports")
                                  ? "Reports Role"
                                  : ""
                          } 
                          placement="right" 
                          arrow
                        >
                          <Box sx={{ width: '100%', px: 2, py: 1 }}>
                            {div.label}
                          </Box>
                        </Tooltip>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "24px",
              marginLeft: "20px",
            }}
          >
            <Tooltip title="FAQs: Coming Soon" arrow>
              <HelpOutlineOutlinedIcon
                style={{ fontSize: 26, color: "white" }}
              />
            </Tooltip>
            {divisionSettings
              ? isDivAdmin && (
                <Tooltip title="Division Settings" arrow>
                  <SettingsOutlinedIcon
                    style={{
                      fontSize: 26,
                      color: "white",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate(ROUTES.settings.division.route)}
                  />
                </Tooltip>
              )
              : null}
            <Tooltip title="Notifications: Coming Soon" arrow>
              <NotificationsActiveOutlinedIcon
                style={{ fontSize: 26, color: "white" }}
              />
            </Tooltip>
          </div>
          <div className="relative" ref={ref}>
            <div
              style={{
                borderRadius: "14px",
                display: "flex",
                marginLeft: "18px",
                height: "30px",
                alignItems: "center",
                padding: "0 3px",
                position: "relative",
              }}
            >
              <Avatar
                alt={userInfo?.abbr}
                sx={{
                  width: 40,
                  height: 40,
                  color: "#000",
                  bgcolor: "#FFF",
                  fontSize: "14px",
                  fontWeight: 600,
                  border: "2px solid #FFF",
                }}
              >
                {userInfo?.abbr}
              </Avatar>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  marginLeft: "8px",
                  lineHeight: 1.1,
                  maxWidth: 170,
                }}
              >
                <p
                  style={{
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#FFF",
                    margin: 0,
                    padding: 0,
                  }}
                  title={userInfo?.name}
                >
                  {userInfo?.name}
                </p>
                <p
                  style={{
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    fontSize: "12px",
                    color: "#DBE3FF",
                    opacity: 0.85,
                    margin: 0,
                    padding: 0,
                  }}
                  title={userInfo?.email}
                >
                  {userInfo?.email}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};
